import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { OpsAuditRecord, OpsAuditTargetType } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useAuditList } from '../api/useAudit.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * `/governance/audit` — append-only ops audit log viewer.
 *
 * DESIGN.md §6 Data tables: zebra via `surface-container` vs
 * `surface-container-low`, no horizontal dividers, `label-md` uppercase
 * column headers.
 *
 * Operator affordances:
 *   - Four independent text filters (action, target type, target id,
 *     ops user id). All combine via AND on the backend.
 *   - Pagination (limit=50). Offset-only; total comes from the response.
 *   - Click a row to expand metadata JSON (pretty-printed).
 *
 * Filters change the React Query cache key so back/forward navigation
 * between filter states rehydrates from cache. No write mutations on
 * this page; the audit log is immutable.
 */

const PAGE_LIMIT = 50;

const TARGET_TYPE_OPTIONS: ReadonlyArray<{
  readonly value: '' | OpsAuditTargetType;
  readonly labelKey: I18nKey;
}> = [
  { value: '', labelKey: 'audit.filter.target_type.any' },
  { value: 'account', labelKey: 'audit.filter.target_type.account' },
  { value: 'gift-card', labelKey: 'audit.filter.target_type.gift_card' },
  { value: 'gift-card-batch', labelKey: 'audit.filter.target_type.gift_card_batch' },
  { value: 'upstream', labelKey: 'audit.filter.target_type.upstream' },
  {
    value: 'upstream-model-mapping',
    labelKey: 'audit.filter.target_type.upstream_model_mapping',
  },
  {
    value: 'upstream-funding-batch',
    labelKey: 'audit.filter.target_type.upstream_funding_batch',
  },
  { value: 'model', labelKey: 'audit.filter.target_type.model' },
  { value: 'failed-deduction', labelKey: 'audit.filter.target_type.failed_deduction' },
  { value: 'exchange-rate-policy', labelKey: 'audit.filter.target_type.exchange_rate_policy' },
  { value: 'payment-order', labelKey: 'audit.filter.target_type.payment_order' },
  { value: 'payment-provider', labelKey: 'audit.filter.target_type.payment_provider' },
];

function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return '—';
  const delta = Date.now() - parsed;
  const seconds = Math.round(delta / 1000);
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/**
 * Pretty-print a metadata JSON string. Falls back to the raw string if
 * the payload doesn't parse — this should never happen with data we
 * write ourselves, but the audit table's `metadata` is TEXT so any
 * content is technically legal. Don't crash the row if an out-of-band
 * writer puts malformed JSON in.
 */
function prettyMetadata(raw: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

interface RowProps {
  readonly row: OpsAuditRecord;
  readonly zebra: 'odd' | 'even';
  readonly expanded: boolean;
  readonly onToggle: (id: string) => void;
}

function AuditRow({ row, zebra, expanded, onToggle }: RowProps): JSX.Element {
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  return (
    <>
      <tr
        data-testid={`audit-row-${row.id}`}
        className={`${zebraClass} cursor-pointer transition-colors hover:bg-surface-bright`}
        onClick={() => onToggle(row.id)}
      >
        <td
          className="px-4 py-3 text-[11px] text-on-surface-variant"
          title={row.created_at}
          data-testid={`audit-row-${row.id}-created`}
        >
          {formatRelative(row.created_at)}
        </td>
        <td
          className="px-4 py-3 text-[12px] font-semibold text-on-surface"
          data-testid={`audit-row-${row.id}-action`}
        >
          {row.action}
        </td>
        <td
          className="px-4 py-3 text-[12px] text-on-surface-variant"
          data-testid={`audit-row-${row.id}-target-type`}
        >
          {row.target_type}
        </td>
        <td
          className="px-4 py-3 font-mono text-[12px] text-on-surface-variant"
          title={row.target_id}
          data-testid={`audit-row-${row.id}-target-id`}
        >
          {truncateId(row.target_id)}
        </td>
        <td
          className="px-4 py-3 font-mono text-[12px] text-on-surface-variant"
          title={row.ops_user_id}
          data-testid={`audit-row-${row.id}-ops-user`}
        >
          {truncateId(row.ops_user_id)}
        </td>
        <td className="px-4 py-3 text-right">
          <span
            aria-hidden="true"
            className="material-symbols-outlined text-[18px] text-outline"
          >
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
        </td>
      </tr>
      {expanded ? (
        <tr
          className={zebraClass}
          data-testid={`audit-row-${row.id}-metadata`}
        >
          <td colSpan={6} className="px-4 pb-4">
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-surface-container-lowest p-3 font-mono text-[11px] text-on-surface">
{prettyMetadata(row.metadata)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

interface FilterState {
  action: string;
  targetType: string;
  targetId: string;
  opsUserId: string;
}

const EMPTY_FILTERS: FilterState = {
  action: '',
  targetType: '',
  targetId: '',
  opsUserId: '',
};

export function Audit(): JSX.Element {
  const t = useT();
  // Draft state is edited in the form; `applied` is what actually
  // drives the query. Pattern matches the accounts list — typing in
  // the search field must not issue a query on every keystroke.
  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useAuditList({
    action: applied.action.length > 0 ? applied.action : undefined,
    target_type: applied.targetType.length > 0 ? applied.targetType : undefined,
    target_id: applied.targetId.length > 0 ? applied.targetId : undefined,
    ops_user_id: applied.opsUserId.length > 0 ? applied.opsUserId : undefined,
    limit: PAGE_LIMIT,
    offset,
  });

  const rows = useMemo(() => query.data?.audit ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setApplied({
      action: draft.action.trim(),
      targetType: draft.targetType.trim(),
      targetId: draft.targetId.trim(),
      opsUserId: draft.opsUserId.trim(),
    });
    setOffset(0);
    setExpandedId(null);
  }

  function handleReset(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
    setExpandedId(null);
  }

  function handleToggle(id: string): void {
    setExpandedId((current) => (current === id ? null : id));
  }

  return (
    <section data-testid="audit-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('audit.title')}
        </h2>
        <p className="text-sm text-outline">{t('audit.subtitle')}</p>
      </header>

      <form
        onSubmit={handleSubmit}
        data-testid="audit-filters"
        className="flex flex-wrap items-end gap-3 rounded-xl bg-surface-container-low p-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('audit.filter.action')}
          </span>
          <input
            type="text"
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.currentTarget.value })}
            placeholder={t('audit.filter.action.placeholder')}
            data-testid="audit-filter-action"
            className="h-9 w-56 rounded-lg bg-surface-container-lowest px-3 text-[12px] text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('audit.filter.target_type')}
          </span>
          <select
            value={draft.targetType}
            onChange={(e) => setDraft({ ...draft, targetType: e.currentTarget.value })}
            data-testid="audit-filter-target-type"
            className="h-9 w-48 rounded-lg bg-surface-container-lowest px-3 text-[12px] text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {TARGET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('audit.filter.target_id')}
          </span>
          <input
            type="text"
            value={draft.targetId}
            onChange={(e) => setDraft({ ...draft, targetId: e.currentTarget.value })}
            placeholder={t('audit.filter.target_id.placeholder')}
            data-testid="audit-filter-target-id"
            className="h-9 w-64 rounded-lg bg-surface-container-lowest px-3 font-mono text-[12px] text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('audit.filter.ops_user_id')}
          </span>
          <input
            type="text"
            value={draft.opsUserId}
            onChange={(e) => setDraft({ ...draft, opsUserId: e.currentTarget.value })}
            placeholder={t('audit.filter.ops_user_id.placeholder')}
            data-testid="audit-filter-ops-user"
            className="h-9 w-52 rounded-lg bg-surface-container-lowest px-3 font-mono text-[12px] text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            data-testid="audit-filter-apply"
            className="inline-flex h-9 items-center justify-center rounded-lg bg-primary-container px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
          >
            {t('audit.filter.apply')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            data-testid="audit-filter-reset"
            className="inline-flex h-9 items-center justify-center rounded-lg bg-surface-container-highest px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('audit.filter.reset')}
          </button>
        </div>
      </form>

      {query.isError ? (
        <div
          role="alert"
          data-testid="audit-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('audit.error.title')}</span>
            <span className="text-xs">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void query.refetch()}
            data-testid="audit-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="audit-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('audit.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <div
          data-testid="audit-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {t('audit.empty.title')}
          </h3>
          <p className="max-w-md text-sm text-outline">{t('audit.empty.body')}</p>
        </div>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <div className="overflow-hidden rounded-xl bg-surface-container">
            <table
              data-testid="audit-table"
              className="w-full border-separate border-spacing-0"
            >
              <thead>
                <tr className="bg-surface-container-high">
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('audit.column.when')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('audit.column.action')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('audit.column.target_type')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('audit.column.target_id')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('audit.column.ops_user')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <AuditRow
                    key={row.id}
                    row={row}
                    zebra={i % 2 === 0 ? 'odd' : 'even'}
                    expanded={expandedId === row.id}
                    onToggle={handleToggle}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <nav
            aria-label={t('accounts.pagination.aria_label')}
            data-testid="audit-pagination"
            className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-low px-6 py-4 text-[12px] text-outline"
          >
            <span>
              {t('audit.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="audit-pagination-prev"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.previous')}
              </button>
              <button
                type="button"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="audit-pagination-next"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.next')}
              </button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
